import { AircraftSchema } from '@/features/end-of-lease/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { toCamelCase, toSnakeCase } from '@/lib/case';

import { useAuthAxios } from '@/hooks/useAuthAxios';

import { AssetBulkDeleteResult } from './types';

export interface CollectionSchema {
    id: number;
    teamId: number;
    name: string;
    description: string | null;
    createdBy: string | null;
    createdAt: string;
    updatedAt: string;
    aircrafts: AircraftSchema[];
}

export const useCollections = (search?: string) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['collections', search],
        queryFn: async () => {
            const response = await client.get('/api/leasing/collections', {
                params: search ? { search } : undefined,
            });
            return toCamelCase<CollectionSchema[]>(response.data);
        },
        enabled,
    });
};

export const useCreateCollection = () => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: { name: string }) => {
            const response = await client.post(
                '/api/leasing/collections',
                toSnakeCase(data),
            );
            return toCamelCase<CollectionSchema>(response.data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['collections'] });
        },
    });
};

export const useAddAircraftToCollection = () => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            collectionId,
            aircraftId,
        }: {
            collectionId: number;
            aircraftId: number;
        }) => {
            const response = await client.post(
                `/api/leasing/collections/${collectionId}/aircrafts/${aircraftId}`,
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['collections'] });
        },
    });
};

export const useCollection = (collectionId: number) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['collections', collectionId],
        queryFn: async () => {
            const response = await client.get(
                `/api/leasing/collections/${collectionId}`,
            );
            return toCamelCase<CollectionSchema>(response.data);
        },
        enabled: enabled && !!collectionId,
    });
};

export const useBulkDeleteAssets = () => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: {
            collectionIds: number[];
            projectIds: number[];
        }) => {
            const res = await client.post('/api/leasing/bulk-delete-assets', {
                collection_ids: input.collectionIds,
                project_ids: input.projectIds,
            });
            return toCamelCase<AssetBulkDeleteResult>(res.data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['collections'] });
            queryClient.invalidateQueries({ queryKey: ['aircrafts'] });
        },
    });
};
